/**
 * Shop drawings — pure helpers (no React). Counting, labels, dates and filters used by the page
 * and its tabs. Unit-tested in src/__tests__/shop-drawings.test.js.
 */
import { ageDays, formatDate, formatDays, parseDateValue, toDateInputValue } from '@/lib/format';
import { ageTone } from '@/lib/status';
import { groupRows, sortRows } from '@/lib/table';

export const STATUSES = ['COMPLETED', 'COMPLETED_WITH_REMARKS', 'BLOCKED', 'IN_PROGRESS', 'NOT_STARTED', 'PENDING', 'SUBMITTED', 'APPROVED', 'APPROVED_WITH_COMMENTS', 'REJECTED', 'CANCELLED', 'ON_HOLD'];
export const BLOCKERS = ['NONE', 'MISSING_DESIGN_INFO', 'PENDING_COORDINATION', 'AWAITING_DRAWINGS', 'NO_DRAWING_ID', 'PENDING', 'SITE_TEAM', 'CONSULTANT_COMMENTS'];
/** Reasons a person can pick for a blocked drawing (everything except NONE). */
export const BLOCKER_REASONS = BLOCKERS.filter((value) => value !== 'NONE');
export const REPLIES = ['PENDING', 'APPROVED', 'APPROVED_WITH_COMMENTS', 'REVISE_AND_RESUBMIT', 'REJECTED'];

export const STATUS_LABELS = {
  COMPLETED: 'Completed',
  COMPLETED_WITH_REMARKS: 'Completed w/ remarks',
  BLOCKED: 'Blocked',
  IN_PROGRESS: 'In progress',
  NOT_STARTED: 'Not started',
  PENDING: 'Pending',
  SUBMITTED: 'Submitted',
  APPROVED: 'Approved',
  APPROVED_WITH_COMMENTS: 'Approved w/ comments',
  REJECTED: 'Rejected',
  CANCELLED: 'Cancelled',
  ON_HOLD: 'On hold',
};

export const BLOCKER_LABELS = {
  NONE: 'None',
  MISSING_DESIGN_INFO: 'Missing design info',
  PENDING_COORDINATION: 'Pending coordination',
  AWAITING_DRAWINGS: 'Awaiting drawings',
  NO_DRAWING_ID: 'No drawing ID',
  PENDING: 'Pending',
  SITE_TEAM: 'Site Team',
  CONSULTANT_COMMENTS: 'Consultant comments',
};

/** Small hint after a reason in pickers (14 #7: the consultant's code C is a reason of its own now). */
export const BLOCKER_HINTS = { CONSULTANT_COMMENTS: 'code C' };
export const NEW_BLOCKERS = new Set(['CONSULTANT_COMMENTS']);

export const REPLY_LABELS = {
  PENDING: 'Awaiting reply',
  APPROVED: 'Approved',
  APPROVED_WITH_COMMENTS: 'Approved w/ comments',
  REVISE_AND_RESUBMIT: 'Revise & resubmit',
  REJECTED: 'Rejected',
};

export const labelize = (value, labels = {}) => labels[value] || String(value || '').replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());

const clean = (value) => String(value ?? '').trim();

/* ------------------------------------------------------------------ drawing identity */

// How the register types "there is no drawing ID yet": '-', 'NO ID', 'NOID', 'NONE', 'NO_ID', …
const PLACEHOLDER_ID_TOKENS = new Set(['', 'NOID', 'NONE', 'NA', 'TBD', 'TBA', 'NIL', 'NULL']);

/** True when a Drawing ID is only a placeholder ('-', 'NO ID', 'NOID', 'NONE', 'NO_ID', 'N/A', …). */
export function isPlaceholderDrawingId(value) {
  return PLACEHOLDER_ID_TOKENS.has(clean(value).toUpperCase().replace(/[^A-Z0-9]/g, ''));
}

/** The real Drawing ID, or '' when it is a placeholder. */
export const realDrawingId = (value) => (isPlaceholderDrawingId(value) ? '' : clean(value));

const isPlaceholderText = (value) => ['', '-', '--', '—', 'N/A', 'NA'].includes(clean(value).toUpperCase());

/** What a person calls the drawing: its description, or the floor / area when the description is '-'. */
export function drawingTitle(drawing = {}) {
  const sameAsId = clean(drawing.desc).toUpperCase() === clean(drawing.drawingId).toUpperCase();
  if (!isPlaceholderText(drawing.desc) && !(sameAsId && !isPlaceholderText(drawing.floor))) return clean(drawing.desc);
  if (!isPlaceholderText(drawing.floor)) return clean(drawing.floor);
  if (!isPlaceholderText(drawing.block)) return clean(drawing.block);
  return realDrawingId(drawing.drawingId) || clean(drawing.id) || 'Untitled drawing';
}

/** 'LUXOR TOWER #69' style project label parts. */
export const projectNumber = (drawing = {}) => clean(drawing.project_no).replace(/^#/, '');

/**
 * A PDF / DWG link that is safe to open: http(s) or a path on this site; anything else ('javascript:…',
 * 'data:…', a typed file name) → '' so it is never rendered as a link.
 */
export function safeHref(value) {
  const text = clean(value);
  if (!text) return '';
  if (/^https?:\/\//i.test(text)) return text;
  if (/^\/(?!\/)/.test(text)) return text;
  return '';
}

/** The labels the edit form uses, for a drawing's activity log ('project_no' → 'Project No.'). */
export const FIELD_LABELS = {
  project_no: 'Project No.',
  project: 'Project',
  disc: 'Discipline',
  block: 'Block / Zone',
  floor: 'Floor / Area',
  dtype: 'Type',
  desc: 'Description',
  drawingId: 'Drawing ID',
  rev: 'Revision',
  status: 'Status',
  blocker: 'Blocker',
  action: 'Action note',
  resp: 'Responsible',
  target: 'Target date',
  updby: 'Updated by',
  pdf: 'PDF link',
  dwg: 'DWG link',
  notes: 'Notes',
};

/** 'project_no' → 'Project No.'; an unknown key reads as words ('reply_date' → 'Reply date'). */
export function fieldLabel(field) {
  const key = clean(field);
  if (FIELD_LABELS[key]) return FIELD_LABELS[key];
  const words = key.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/_/g, ' ').trim().toLowerCase();
  return words ? words[0].toUpperCase() + words.slice(1) : 'Field';
}

/** A changed value as people read it: status / blocker codes as their labels, the rest as stored. */
export function fieldValueText(field, value) {
  const text = clean(value);
  if (!text) return '';
  if (field === 'status') return labelize(text.toUpperCase(), STATUS_LABELS);
  if (field === 'blocker') return labelize(text.toUpperCase(), BLOCKER_LABELS);
  // 'submission_reply', 'reply': the reply codes read as on the Submission Log ('PENDING' → 'Awaiting reply').
  if (/(^|_)reply$/.test(clean(field)) && REPLY_LABELS[text.toUpperCase()]) return REPLY_LABELS[text.toUpperCase()];
  return text;
}

/* ------------------------------------------------------------------ pipeline */

/**
 * The drawing pipeline in workflow order. Every one of the 12 backend statuses lands in exactly one
 * group (the old KPI cards counted only 5 of them). Optional groups appear only when non-empty.
 */
export const PIPELINE_GROUPS = [
  { key: 'approved', label: 'Approved & completed', statuses: ['APPROVED', 'COMPLETED'], tone: 'navy', approved: true },
  { key: 'approved_comments', label: 'Approved w/ comments', statuses: ['APPROVED_WITH_COMMENTS', 'COMPLETED_WITH_REMARKS'], tone: 'teal', approved: true },
  { key: 'submitted', label: 'Submitted · awaiting consultant', short: 'Awaiting consultant', statuses: ['SUBMITTED'], tone: 'amber' },
  { key: 'in_progress', label: 'In progress', statuses: ['IN_PROGRESS', 'PENDING'], tone: 'blue' },
  { key: 'not_started', label: 'Not started', statuses: ['NOT_STARTED'], tone: 'gray' },
  { key: 'on_hold', label: 'On hold', statuses: ['ON_HOLD'], tone: 'amber', pattern: 'hold' },
  { key: 'rejected', label: 'Rejected', statuses: ['REJECTED'], tone: 'red', pattern: 'rejected', optional: true },
  { key: 'blocked', label: 'Blocked', statuses: ['BLOCKED'], tone: 'red' },
  { key: 'cancelled', label: 'Cancelled', statuses: ['CANCELLED'], tone: 'outline', optional: true },
  { key: 'other', label: 'Other status', statuses: [], tone: 'outline', optional: true },
];

const GROUP_BY_STATUS = new Map(PIPELINE_GROUPS.flatMap((group) => group.statuses.map((status) => [status, group.key])));

export const pipelineGroupOf = (status) => GROUP_BY_STATUS.get(clean(status).toUpperCase()) || 'other';
export const getPipelineGroup = (key) => PIPELINE_GROUPS.find((group) => group.key === key) || null;

/** { total, projects, groups: [{ ...group, count }], approvedCount, approvedPct } */
export function computePipeline(drawings = []) {
  const counts = Object.fromEntries(PIPELINE_GROUPS.map((group) => [group.key, 0]));
  drawings.forEach((drawing) => { counts[pipelineGroupOf(drawing.status)] += 1; });
  const total = drawings.length;
  const groups = PIPELINE_GROUPS
    .map((group) => ({ ...group, count: counts[group.key] }))
    .filter((group) => !group.optional || group.count > 0);
  const approvedCount = groups.filter((group) => group.approved).reduce((sum, group) => sum + group.count, 0);
  return {
    total,
    projects: countProjects(drawings),
    groups,
    approvedCount,
    approvedPct: total ? Math.round((approvedCount / total) * 100) : 0,
  };
}

export const countProjects = (drawings = []) => new Set(drawings.map((drawing) => clean(drawing.project).toLowerCase()).filter(Boolean)).size;

/** The newest change in the register: { when: '16 Sep 2026', who: 'Yahya' } or null. */
export function latestChange(drawings = []) {
  let best = null;
  drawings.forEach((drawing) => {
    const stamp = clean(drawing.updated_at) || clean(drawing.created_at);
    const time = Date.parse(stamp);
    if (!Number.isFinite(time)) return;
    if (!best || time > best.time) best = { time, stamp, who: clean(drawing.updby) };
  });
  if (!best) return null;
  return { when: formatDate(best.stamp), who: best.who };
}

/* ------------------------------------------------------------------ people */

const AVATAR_COLORS = ['#3d3da3', '#0f766e', '#b45309', '#be185d', '#4d7c0f', '#6d28d9'];

/** 'mohanad' → 'MO'; 'Awady Mohammed' → 'AM'; 'YM' → 'YM'. */
export function personInitials(name) {
  const words = clean(name).split(/\s+/).filter(Boolean);
  if (!words.length) return '?';
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
}

/** Stable colour per person (same name → same colour everywhere). */
export function personColor(name) {
  const key = clean(name).toLowerCase();
  let hash = 0;
  for (let index = 0; index < key.length; index += 1) hash = ((hash * 31) + key.charCodeAt(index)) >>> 0;
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
}

/** Display form of a person name ('mohanad' → 'Mohanad'); codes like 'YM' are kept. */
export function displayPerson(name) {
  const text = clean(name);
  if (!text) return '';
  if (text === text.toUpperCase() && text.length <= 4) return text;
  return text.replace(/\b\w/g, (char) => char.toUpperCase());
}

/* ------------------------------------------------------------------ blockers */

/** Reason badge meta: a blocked drawing with blocker NONE is "Reason missing" (amber), never "None". */
export function blockerMeta(drawing = {}) {
  const blocker = clean(drawing.blocker).toUpperCase() || 'NONE';
  if (blocker === 'NONE') {
    return drawing.status === 'BLOCKED'
      ? { key: 'REASON_MISSING', label: 'Reason missing', tone: 'amber', missing: true }
      : { key: 'NONE', label: '', tone: 'gray', missing: false };
  }
  return { key: blocker, label: labelize(blocker, BLOCKER_LABELS), tone: 'red', missing: false };
}

/** Action notes that say nothing ('', '-', 'NONE', 'N/A'). */
export const isEmptyNote = (value) => ['', '-', '--', '—', 'NONE', 'N/A', 'NA', 'NIL'].includes(clean(value).toUpperCase());

export const isBlocked = (drawing) => clean(drawing?.status).toUpperCase() === 'BLOCKED';

/** Pareto of blocked reasons: [{ key, label, tone, count }] sorted by count (Reason missing first on ties). */
export function blockedReasonCounts(drawings = []) {
  const counts = new Map();
  drawings.filter(isBlocked).forEach((drawing) => {
    const meta = blockerMeta(drawing);
    const entry = counts.get(meta.key) || { key: meta.key, label: meta.label, tone: meta.tone, count: 0 };
    entry.count += 1;
    counts.set(meta.key, entry);
  });
  return [...counts.values()].sort((a, b) => (b.count - a.count) || (a.key === 'REASON_MISSING' ? -1 : 1));
}

/* ------------------------------------------------------------------ submission dates */

const MONTH_ONLY = /^([a-z]{3,9})\.?[\s./-]*(\d{4})$/i;
const MONTHS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];

/**
 * A future date whose day and month, swapped, give a past date (the usual Excel mix-up between
 * 12/06/2026 and 06/12/2026) → { iso, label } to suggest; otherwise null.
 */
function swappedSuggestion(iso, { now } = {}) {
  const match = String(iso || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;
  const [, year, month, day] = match;
  if (month === day || Number(day) > 12) return null;
  const swapped = `${year}-${day}-${month}`;
  const age = ageDays(swapped, { now });
  return age !== null && age >= 0 ? { iso: swapped, label: formatDate(swapped) } : null;
}

/**
 * Read a free-text 'Date sent' ('18/08/2023', '46362', '30/5/2025', '14-05-2026', '03.09.2025',
 * 'JULY.2025', ISO) into one shape:
 *   { ok, iso: 'YYYY-MM-DD' | null, label: '30 May 2025', age: 488 | null, needsCheck, reason }
 * needsCheck flags dates people must look at: in the future (Excel serials like 46362 = 6 Dec 2026),
 * month only (JULY.2025), unreadable or missing.
 */
export function readSentDate(value, { now } = {}) {
  const raw = clean(value);
  if (!raw) return { ok: false, iso: null, label: 'No date', age: null, needsCheck: true, reason: 'No date recorded', raw };
  const parsed = parseDateValue(raw);
  if (parsed) {
    const iso = toDateInputValue(raw);
    const age = ageDays(raw, { now });
    const label = formatDate(raw);
    const excel = /^\d{5}(\.\d+)?$/.test(raw);
    if (age !== null && age < 0) {
      return {
        ok: true, iso, label, age, needsCheck: true, raw,
        reason: excel ? `Typed as the Excel number ${raw} (= ${label}, in the future)` : 'Date is in the future',
        suggestion: swappedSuggestion(iso, { now }),
      };
    }
    return { ok: true, iso, label, age, needsCheck: false, reason: '', raw };
  }
  const month = raw.match(MONTH_ONLY);
  if (month) {
    const index = MONTHS.indexOf(month[1].slice(0, 3).toLowerCase());
    if (index > -1) {
      const iso = `${month[2]}-${String(index + 1).padStart(2, '0')}-01`;
      const label = `${MONTHS[index][0].toUpperCase()}${MONTHS[index].slice(1)} ${month[2]}`;
      // `iso` (the 1st) only places the row when sorting; the day itself is unknown (monthOnly).
      return { ok: true, iso, label, age: ageDays(iso, { now }), needsCheck: true, monthOnly: true, reason: 'Month only, no day', raw };
    }
  }
  return { ok: false, iso: null, label: raw, age: null, needsCheck: true, reason: 'Not a readable date', raw };
}

/** Age text colour per the shared rule: red above 90 d, amber 31–90 d, ink-2 otherwise. */
export const ageToneOf = (days) => ageTone(days);

/** Age bucket used by the "Date sent" filter. */
export function sentBucket(sent) {
  if (sent.needsCheck) return 'check';
  if (sent.age === null) return 'check';
  if (sent.age <= 30) return 'recent';
  if (sent.age <= 90) return 'month';
  if (sent.age <= 365) return 'quarter';
  return 'year';
}

export const SENT_BUCKETS = [
  { value: 'recent', label: 'Last 30 days' },
  { value: 'month', label: '31–90 days ago' },
  { value: 'quarter', label: '91–365 days ago' },
  { value: 'year', label: 'Over a year ago' },
  { value: 'check', label: 'Date needs check' },
];

export const IMPORT_COMMENT = 'Imported from Submitted = YES';

/** Statuses that mean the consultant approved the drawing (the import's "Approved" is plausible there). */
const APPROVED_STATUSES = new Set(['APPROVED', 'COMPLETED', 'APPROVED_WITH_COMMENTS', 'COMPLETED_WITH_REMARKS']);

/**
 * One row per logged submission. Reply state is honest about the Excel import:
 *  - `awaiting`: reply PENDING, or an import "Approved" on a drawing that is still Submitted
 *    (`importUnfixed` — the banner offers to store it as Awaiting reply);
 *  - `not_recorded`: an import "Approved" on a drawing that is Blocked / In progress / … (nobody ever
 *    recorded a reply);
 *  - `replied`: a real reply (or the import's "Approved" on a drawing that is approved).
 * `transmittalKey` groups what was sent together: same project, same day, same sender.
 */
export function buildSubmissionRows(drawings = [], { now } = {}) {
  return drawings.flatMap((drawing) => {
    const submissions = drawing.submissions || [];
    return submissions.map((submission, index) => {
      const sent = readSentDate(submission.date, { now });
      const comments = clean(submission.comments);
      const fromImport = comments === IMPORT_COMMENT;
      const storedReply = clean(submission.reply).toUpperCase() || 'PENDING';
      const replyDate = clean(submission.reply_date);
      const status = clean(drawing.status).toUpperCase();
      const importUnfixed = fromImport && storedReply === 'APPROVED' && !clean(submission.import_reply) && status === 'SUBMITTED';
      let replyState = 'replied';
      if (storedReply === 'PENDING' || importUnfixed) replyState = 'awaiting';
      else if (fromImport && !replyDate && !APPROVED_STATUSES.has(status)) replyState = 'not_recorded';
      return {
        key: submission.id || `${drawing.id}-${index}`,
        index,
        drawing,
        submission,
        sent,
        reply: storedReply,
        replyState,
        importUnfixed,
        importReply: clean(submission.import_reply).toUpperCase() || (importUnfixed || replyState === 'not_recorded' ? storedReply : ''),
        replyDate: replyDate ? readSentDate(replyDate, { now }) : null,
        replyComments: clean(submission.reply_comments),
        isLatest: index === submissions.length - 1,
        fromImport,
        comments: fromImport ? '' : comments,
        by: clean(submission.by),
        to: clean(submission.to),
        transmittalKey: [
          valueKey(drawing.project) || '—',
          sent.iso || `raw:${sent.raw}`,
          valueKey(submission.by) || '—',
        ].join('|'),
      };
    });
  });
}

/** Sort submission rows by date sent (rows without a readable date go last). */
export function sortSubmissionRows(rows, direction = 'asc') {
  const sign = direction === 'desc' ? -1 : 1;
  return [...rows].sort((a, b) => {
    if (!a.sent.iso && !b.sent.iso) return 0;
    if (!a.sent.iso) return 1;
    if (!b.sent.iso) return -1;
    return a.sent.iso < b.sent.iso ? -sign : a.sent.iso > b.sent.iso ? sign : 0;
  });
}

/* ------------------------------------------------------------------ search / options */

export function drawingSearchText(drawing) {
  return [
    drawing.id,
    drawing.project_no,
    drawing.project,
    drawing.disc,
    drawing.block,
    drawing.floor,
    drawing.desc,
    drawing.dtype,
    drawing.drawingId,
    drawing.rev,
    labelize(drawing.status, STATUS_LABELS),
    labelize(drawing.blocker, BLOCKER_LABELS),
    drawing.action,
    drawing.resp,
    drawing.target,
    drawing.updby,
    drawing.updated,
    drawing.notes,
    drawing.pdf,
    drawing.dwg,
  ].filter(Boolean).join(' ').toLowerCase();
}

export const uniqueValues = (rows, field) => [...new Set(rows.map((row) => clean(row[field])).filter(Boolean))].sort((a, b) => a.localeCompare(b));

/**
 * Key that groups free-text spellings of one value: 'LIV WATERSIDE' and 'Liv  Waterside' → 'liv waterside'.
 * Filter options are keyed by it and filters compare with it, so counts and matches always agree.
 */
export const valueKey = (value) => clean(value).replace(/\s+/g, ' ').toLowerCase();

/** Does a free-text field match a filter value (an option key, or a raw spelling)? */
export const sameValue = (value, filterValue) => valueKey(value) === valueKey(filterValue);

/**
 * [{ value: key, label, count }] of a field, most common first. Spellings that differ only in case
 * or spacing are one option, labelled with the most used spelling (through `labelOf`).
 */
export function optionCounts(rows, read, { labelOf = (value) => value, emptyLabel } = {}) {
  const groups = new Map();
  rows.forEach((row) => {
    const raw = clean(read(row));
    if (!raw && !emptyLabel) return;
    const key = raw ? valueKey(raw) : '__empty__';
    const group = groups.get(key) || { count: 0, spellings: new Map() };
    group.count += 1;
    if (raw) group.spellings.set(raw, (group.spellings.get(raw) || 0) + 1);
    groups.set(key, group);
  });
  return [...groups.entries()]
    .map(([value, group]) => {
      const spelling = [...group.spellings.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] ?? '';
      return { value, label: value === '__empty__' ? emptyLabel : labelOf(spelling), count: group.count };
    })
    .sort((a, b) => (b.count - a.count) || String(a.label).localeCompare(String(b.label)));
}

/* ------------------------------------------------------------------ paging */

export const PAGE_SIZES = [25, 50, 100];
/** All Drawings grouped by project: projects per page (13 #5, "Showing 5 of 39 projects"). */
export const GROUP_PAGE_SIZES = [5, 10, 20];
/** Rows an open project group shows before its "more in <project>" row (13 #5: ~4 + "+ 26 more"). */
export const GROUP_ROW_LIMIT = 4;

/**
 * Which groups of a page start open: in order while the rows they SHOW (each at most `rowLimit`) fit in
 * `budget` — so short groups do not use up the budget of the long ones. Always the first.
 */
export function defaultOpenOnPage(groups = [], { rowLimit = GROUP_ROW_LIMIT, budget = 12 } = {}) {
  return defaultOpenGroups(groups.map((group) => ({ key: group.key, count: Math.min(rowLimit, group.rows ? group.rows.length : group.count || 0) })), budget);
}

export function paginate(rows, page, pageSize) {
  const pageCount = Math.max(1, Math.ceil(rows.length / pageSize));
  const safePage = Math.min(Math.max(1, page), pageCount);
  const start = (safePage - 1) * pageSize;
  return {
    page: safePage,
    pageCount,
    rows: rows.slice(start, start + pageSize),
    from: rows.length ? start + 1 : 0,
    to: Math.min(start + pageSize, rows.length),
    total: rows.length,
  };
}

/** [1, 2, 3, 'gap-15', 15] — page buttons with gaps. */
export function pageWindow(page, pageCount) {
  if (pageCount <= 7) return Array.from({ length: pageCount }, (_, index) => index + 1);
  const pages = new Set([1, pageCount, page, page - 1, page + 1]);
  if (page <= 3) [2, 3, 4].forEach((value) => pages.add(value));
  if (page >= pageCount - 2) [pageCount - 1, pageCount - 2, pageCount - 3].forEach((value) => pages.add(value));
  const sorted = [...pages].filter((value) => value >= 1 && value <= pageCount).sort((a, b) => a - b);
  const out = [];
  sorted.forEach((value, index) => {
    if (index > 0 && value - sorted[index - 1] > 1) out.push(`gap-${value}`);
    out.push(value);
  });
  return out;
}

/** 'DD/MM/YYYY' (the register's stored format) from a date-picker value 'YYYY-MM-DD'. */
export function toDayFirst(isoValue) {
  const match = clean(isoValue).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return match ? `${match[3]}/${match[2]}/${match[1]}` : clean(isoValue);
}

/** Today in the UAE as 'YYYY-MM-DD' (for date pickers' default and max). */
export const todayIso = (now) => toDateInputValue(now ? new Date(now) : new Date());

/* ------------------------------------------------------------------ tab filters */

const lower = (value) => clean(value).toLowerCase();

/** Blocked tab: status BLOCKED only, then reason / project / discipline / owner / search. */
export function filterBlocked(drawings = [], filters = {}) {
  const query = lower(filters.search);
  return drawings.filter((drawing) => (
    isBlocked(drawing)
    && (!filters.reason || blockerMeta(drawing).key === filters.reason)
    && (!filters.project || sameValue(drawing.project, filters.project))
    && (!filters.disc || sameValue(drawing.disc, filters.disc))
    && (!filters.owner || (filters.owner === '__none__' ? !clean(drawing.resp) : sameValue(drawing.resp, filters.owner)))
    && (!query || drawingSearchText(drawing).includes(query))
  ));
}

const submissionSearchText = (row) => [
  drawingSearchText(row.drawing),
  row.sent.label,
  row.sent.raw,
  row.by,
  row.to,
  row.comments,
  labelize(row.reply, REPLY_LABELS),
].join(' ').toLowerCase();

/** Submission Log: project / discipline / sent by / date-sent bucket / search. */
export function filterSubmissionRows(rows = [], filters = {}) {
  const query = lower(filters.search);
  return rows.filter((row) => (
    (!filters.project || sameValue(row.drawing.project, filters.project))
    && (!filters.disc || sameValue(row.drawing.disc, filters.disc))
    && (!filters.by || sameValue(row.by, filters.by))
    && (!filters.bucket || sentBucket(row.sent) === filters.bucket)
    && (!query || submissionSearchText(row).includes(query))
  ));
}

/** Rows whose date needs a look come first; the rest sorted by date sent. */
export function orderSubmissionRows(rows = [], direction = 'asc') {
  const flagged = rows.filter((row) => row.sent.needsCheck);
  const dated = rows.filter((row) => !row.sent.needsCheck);
  return [...sortSubmissionRows(flagged, 'asc'), ...sortSubmissionRows(dated, direction)];
}

/* ------------------------------------------------------------------ wave 2: All Drawings */

/** The statuses offered by the row's "Change status" popover, in workflow order. */
export const QUICK_STATUSES = ['NOT_STARTED', 'IN_PROGRESS', 'SUBMITTED', 'APPROVED', 'APPROVED_WITH_COMMENTS', 'BLOCKED', 'ON_HOLD'];

/** Status order for sorting the Status column (workflow order). */
const STATUS_ORDER = ['NOT_STARTED', 'PENDING', 'IN_PROGRESS', 'BLOCKED', 'ON_HOLD', 'SUBMITTED', 'REJECTED', 'APPROVED_WITH_COMMENTS', 'COMPLETED_WITH_REMARKS', 'APPROVED', 'COMPLETED', 'CANCELLED'];
export const statusRank = (status) => {
  const index = STATUS_ORDER.indexOf(clean(status).toUpperCase());
  return index === -1 ? STATUS_ORDER.length : index;
};

/** Time of the drawing's last change (ms) — updated_at, else created_at, else the 'Updated' day. */
export function updatedTime(drawing = {}) {
  const stamp = Date.parse(clean(drawing.updated_at) || clean(drawing.created_at));
  if (Number.isFinite(stamp)) return stamp;
  const iso = toDateInputValue(clean(drawing.updated));
  return iso ? Date.parse(`${iso}T00:00:00Z`) : null;
}

/** The latest logged submission of a drawing (or null). */
export const latestSubmission = (drawing = {}) => {
  const list = drawing.submissions || [];
  return list.length ? list[list.length - 1] : null;
};

/**
 * The small line under a drawing's status: why it is where it is.
 *   Submitted → 'sent 23 May · 130 d, no reply' / 'no submission logged yet' (amber)
 *   a blocker reason → 'Blocker: Awaiting drawings' (red); Blocked without one → 'Reason missing' (amber)
 * → { text, tone } or null.
 */
export function statusNote(drawing = {}, { now } = {}) {
  const status = clean(drawing.status).toUpperCase();
  const blocker = clean(drawing.blocker).toUpperCase() || 'NONE';
  if (status === 'BLOCKED' && blocker === 'NONE') return { text: 'Reason missing', tone: 'amber' };
  if (blocker !== 'NONE' && !APPROVED_STATUSES.has(status)) return { text: `Blocker: ${labelize(blocker, BLOCKER_LABELS)}`, tone: 'red' };
  if (status !== 'SUBMITTED') return null;
  const latest = latestSubmission(drawing);
  if (!latest) return { text: 'no submission logged yet', tone: 'amber' };
  const sent = readSentDate(latest.date, { now });
  const when = sent.iso && !sent.monthOnly ? formatDate(sent.iso, { year: 'auto', now }) : sent.label;
  const reply = clean(latest.reply).toUpperCase() || 'PENDING';
  const waiting = reply === 'PENDING' || (clean(latest.comments) === IMPORT_COMMENT && !clean(latest.reply_date));
  if (!waiting) return { text: `sent ${when} · reply ${labelize(reply, REPLY_LABELS)}`, tone: 'gray' };
  if (sent.needsCheck || sent.age === null) return { text: `sent ${when} · date needs check`, tone: 'amber' };
  return { text: `sent ${when} · ${formatDays(sent.age)}, no reply`, tone: sent.age > 90 ? 'red' : 'amber' };
}

// Spellings of a Drawing Type that are the same type ('SCHIMATIC' is a typo of 'Schematic').
const DRAWING_TYPE_ALIASES = { schimatic: 'schematic', shematic: 'schematic' };

/** Key that merges Drawing Type spellings: 'FLOOR PLAN' and 'Floor Plan' → 'floor plan'. */
export const drawingTypeKey = (value) => {
  const key = valueKey(value);
  return DRAWING_TYPE_ALIASES[key] || key;
};

/**
 * [{ value: key, label, count }] for a free-text field, most common first, spellings merged by
 * `keyOf` (default valueKey) and labelled with the most used spelling. Empty values become one
 * muted `emptyLabel` option (value `emptyValue`, '__none__'), listed last.
 */
export function mergedOptions(rows, read, { keyOf = valueKey, labelOf = (value) => value, emptyLabel, emptyValue = '__none__' } = {}) {
  const groups = new Map();
  rows.forEach((row) => {
    const raw = clean(read(row));
    if (!raw && !emptyLabel) return;
    const key = raw ? keyOf(raw) : emptyValue;
    const group = groups.get(key) || { count: 0, spellings: new Map() };
    group.count += 1;
    if (raw) group.spellings.set(raw, (group.spellings.get(raw) || 0) + 1);
    groups.set(key, group);
  });
  return [...groups.entries()]
    .map(([value, group]) => {
      const spelling = [...group.spellings.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] ?? '';
      const option = { value, label: value === emptyValue ? emptyLabel : labelOf(spelling), count: group.count };
      if (value === emptyValue) option.muted = true;
      return option;
    })
    .sort((a, b) => (Number(a.value === emptyValue) - Number(b.value === emptyValue)) || (b.count - a.count) || String(a.label).localeCompare(String(b.label)));
}

/**
 * All Drawings filters (multi-select arrays of option keys; '__none__' = empty field). `nolog`: only drawings
 * without a logged submission (the Submission Log's "68 Submitted not logged" link sets it with Submitted).
 */
export const EMPTY_ALL_FILTERS = { q: '', project: [], disc: [], status: [], resp: [], dtype: [], blocker: [], updby: [], stage: '', nolog: false };

const hasSubmission = (drawing) => (drawing?.submissions || []).length > 0;

/** Drawings that are Submitted but have no submission logged (15: "68 Submitted not logged"). */
export const submittedNotLogged = (drawings = []) => drawings.filter((drawing) => clean(drawing.status).toUpperCase() === 'SUBMITTED' && !hasSubmission(drawing));

/** Is any All Drawings filter set (search, chips, pipeline stage, "No submission logged")? */
export const anyAllFilter = (filters = {}) => Boolean(
  filters.q || filters.stage || filters.nolog
  || ['project', 'disc', 'status', 'resp', 'dtype', 'blocker', 'updby'].some((key) => (filters[key] || []).length)
);

const inList = (list, key) => !list || !list.length || list.includes(key);
const personKey = (value) => (clean(value) ? valueKey(value) : '__none__');

/** Filter the register by the All Drawings toolbar (search, chips, pipeline stage). */
export function filterDrawings(drawings = [], filters = {}) {
  const query = lower(filters.q);
  // Project values are option keys (valueKey); a hand-written link (?project=Ajmal%20Makan) matches too.
  const projects = filters.project?.length ? filters.project.map((value) => (value === '__none__' ? value : valueKey(value))) : filters.project;
  return drawings.filter((drawing) => (
    (!filters.stage || pipelineGroupOf(drawing.status) === filters.stage)
    && inList(projects, valueKey(drawing.project) || '__none__')
    && inList(filters.disc, clean(drawing.disc).toUpperCase() || 'OTHER')
    && inList(filters.status, clean(drawing.status).toUpperCase())
    && inList(filters.resp, personKey(drawing.resp))
    && inList(filters.dtype, drawingTypeKey(drawing.dtype) || '__none__')
    && inList(filters.blocker, clean(drawing.blocker).toUpperCase() || 'NONE')
    && inList(filters.updby, personKey(drawing.updby))
    && (!filters.nolog || !hasSubmission(drawing))
    && (!query || drawingSearchText(drawing).includes(query))
  ));
}

/** Counts of the pipeline groups in a list: { approved: 16, submitted: 14, … }. */
export function pipelineCounts(drawings = []) {
  const counts = {};
  drawings.forEach((drawing) => {
    const key = pipelineGroupOf(drawing.status);
    counts[key] = (counts[key] || 0) + 1;
  });
  return counts;
}

/** Project summary for a group row: segments in pipeline order + approved share. */
export function projectSummary(drawings = []) {
  const counts = pipelineCounts(drawings);
  const segments = PIPELINE_GROUPS
    .filter((group) => counts[group.key])
    .map((group) => ({ key: group.key, label: group.short || group.label, tone: group.tone, pattern: group.pattern, count: counts[group.key] }));
  const approved = PIPELINE_GROUPS.filter((group) => group.approved).reduce((sum, group) => sum + (counts[group.key] || 0), 0);
  const total = drawings.length;
  return { total, segments, approved, approvedPct: total ? Math.round((approved / total) * 100) : 0 };
}

/** Most used non-empty value of a field in a list ('68' for NOBU's project no.). */
export function mostCommon(rows, read) {
  const counts = new Map();
  rows.forEach((row) => {
    const value = clean(read(row));
    if (value) counts.set(value, (counts.get(value) || 0) + 1);
  });
  return [...counts.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] || '';
}

/** Disciplines used in a list, most drawings first: ['LCS', 'HA', 'BMS']. */
export function disciplinesOf(rows) {
  const counts = new Map();
  rows.forEach((row) => {
    const disc = clean(row.disc).toUpperCase() || 'OTHER';
    counts.set(disc, (counts.get(disc) || 0) + 1);
  });
  return [...counts.entries()].sort((a, b) => b[1] - a[1]).map(([disc]) => disc);
}

/**
 * Which groups start open: groups in display order until `budget` rows are open (always the first).
 * The rest start collapsed, so a 39-project register reads as 39 lines + the first projects' rows.
 */
export function defaultOpenGroups(groups = [], budget = 100) {
  const open = [];
  let used = 0;
  for (const group of groups) {
    const count = group.rows ? group.rows.length : group.count || 0;
    if (open.length && used + count > budget) break;
    open.push(group.key);
    used += count;
  }
  return open;
}

/** Group sorted rows exactly like DataTable does (first appearance, then `sortGroups`). */
export function orderedGroups(rows, groupBy, sortGroups) {
  const groups = groupRows(rows, groupBy);
  return sortGroups ? [...groups].sort(sortGroups) : groups;
}

/** Sort rows with column definitions ({ key, sortValue }) — empty values last (lib/table). */
export function sortWithColumns(rows, sort, columns) {
  if (!sort || !sort.key) return rows;
  const byKey = new Map(columns.map((column) => [column.key, column]));
  return sortRows(rows, sort, (row, key) => {
    const column = byKey.get(key);
    return column?.sortValue ? column.sortValue(row) : row?.[key];
  });
}

/* ------------------------------------------------------------------ wave 2: Blocked */

/**
 * Since when a drawing is blocked: the last history change that set its status to Blocked, else the
 * day it was created (imported). → { iso, days, source: 'history' | 'created' } or null.
 */
export function blockedSince(drawing = {}, { now } = {}) {
  const history = drawing.history || [];
  for (let index = history.length - 1; index >= 0; index -= 1) {
    const entry = history[index] || {};
    const toBlocked = (entry.changes || []).some((change) => clean(change?.field).toLowerCase() === 'status' && clean(change?.to).toUpperCase() === 'BLOCKED');
    if (toBlocked) {
      const iso = toDateInputValue(clean(entry.when));
      if (iso) return { iso, days: ageDays(iso, { now }), source: 'history' };
    }
  }
  const created = toDateInputValue(clean(drawing.created_at));
  if (created) {
    // No status history: the age counts from the day the drawing was added — for most of the register the
    // Excel import (its 'create' entry says "Imported from Excel"), which the screen must say (14 #6).
    const imported = history.some((entry) => clean(entry?.type).toLowerCase() === 'create' && /^imported from excel/i.test(clean(entry?.note)));
    return { iso: created, days: ageDays(created, { now }), source: 'created', imported };
  }
  return null;
}

/**
 * Words for a "Blocked for" date (14 #6): a real date to Blocked reads 'since 16 Sep'; the created_at fallback
 * says what it is — 'since import' (the Excel import) or 'since added'.
 * → { sub, group, title, fallback }
 */
export function blockedSinceText(since) {
  if (!since) return null;
  const day = formatDate(since.iso, { year: 'auto' });
  const full = formatDate(since.iso);
  if (since.source !== 'created') {
    return { sub: `since ${day}`, group: `since ${full}`, title: `Blocked on ${full}`, fallback: false };
  }
  if (since.imported) {
    return {
      sub: 'since import',
      group: `since the ${full} import`,
      title: `No status change to Blocked in its history: counted from the Excel import (${full})`,
      fallback: true,
    };
  }
  return {
    sub: 'since added',
    group: `since added ${full}`,
    title: `No status change to Blocked in its history: counted from when it was added (${full})`,
    fallback: true,
  };
}

/** Footer note when some ages count from the import / the day a drawing was added ('' when none do). */
export function blockedFallbackNote(sinceList = []) {
  const fallbacks = sinceList.filter((since) => since && since.source === 'created');
  if (!fallbacks.length) return '';
  const imported = fallbacks.filter((since) => since.imported);
  if (imported.length) {
    const days = [...new Set(imported.map((since) => since.iso))];
    const when = days.length === 1 ? `the ${formatDate(days[0], { year: 'auto' })} import` : 'the import';
    return imported.length === fallbacks.length
      ? `age counts from ${when} where a drawing has no status history`
      : `age counts from ${when} (or the day it was added) where a drawing has no status history`;
  }
  return 'age counts from the day it was added where a drawing has no status history';
}

/** Sort values of the Blocked view's sortable columns (the tab and its Excel export share them). */
export const blockedSortValues = (daysOf) => ({
  drawing: (drawing) => drawingTitle(drawing).toLowerCase(),
  reason: (drawing) => blockerMeta(drawing).label,
  owner: (drawing) => displayPerson(drawing.resp),
  since: daysOf,
});

/** Blocked view groups sorted by "Blocked for": the project whose oldest drawing waits longest first. */
export const blockedGroupSort = (daysOf, dir = 'desc') => {
  const maxDays = (group) => Math.max(-1, ...group.rows.map((drawing) => daysOf(drawing) ?? -1));
  return (a, b) => ((maxDays(b) - maxDays(a)) * (dir === 'desc' ? 1 : -1)) || (b.rows.length - a.rows.length) || String(a.label).localeCompare(String(b.label));
};

/**
 * The drawings of the Blocked view in the order the screen shows them (14 #8 "Export this view"): sorted by
 * `sortValue` ('since:desc' = longest blocked first, 'owner:asc', …), grouped by project — with "Blocked for",
 * the projects whose oldest drawing waits longest come first.
 */
export function blockedViewOrder(rows = [], sortValue = 'since:desc', { now } = {}) {
  const since = new Map(rows.map((drawing) => [drawing.id, blockedSince(drawing, { now })]));
  const daysOf = (drawing) => since.get(drawing.id)?.days ?? null;
  const [key, dir = 'asc'] = String(sortValue || 'since:desc').split(':');
  const values = blockedSortValues(daysOf);
  const sorted = sortRows(rows, { key, dir }, (row, column) => (values[column] ? values[column](row) : row?.[column]));
  const groups = groupRows(sorted, (drawing) => valueKey(drawing.project) || '__none__')
    .map((group) => ({ ...group, label: mostCommon(group.rows, (drawing) => drawing.project) || 'No project' }));
  const ordered = key === 'since' ? [...groups].sort(blockedGroupSort(daysOf, dir)) : groups;
  return ordered.flatMap((group) => group.rows);
}

/** Statuses offered by "Unblock ▾" (where a drawing goes when it is no longer stuck). */
export const UNBLOCK_STATUSES = ['IN_PROGRESS', 'SUBMITTED', 'APPROVED', 'NOT_STARTED'];

/** Blocked drawings that are "the same thing" (same project + same description): Villa 3's 23 rooms. */
export function siblingsOf(drawing, drawings = []) {
  const project = valueKey(drawing.project);
  const desc = valueKey(drawing.desc);
  if (!desc || ['-', '—', 'n/a'].includes(desc)) return [drawing];
  return drawings.filter((other) => isBlocked(other) && valueKey(other.project) === project && valueKey(other.desc) === desc);
}

/* ------------------------------------------------------------------ wave 2: Submission Log */

/** Consultant reply codes as written on a returned transmittal. */
export const REPLY_CODES = [
  { code: 'A', value: 'APPROVED', label: 'Approved', tone: 'navy' },
  { code: 'B', value: 'APPROVED_WITH_COMMENTS', label: 'Approved with comments', tone: 'teal' },
  { code: 'C', value: 'REVISE_AND_RESUBMIT', label: 'Revise & resubmit', tone: 'amber' },
  { code: 'D', value: 'REJECTED', label: 'Rejected', tone: 'red' },
];
export const REPLY_CODE_OF = Object.fromEntries(REPLY_CODES.map((item) => [item.value, item.code]));

/** What recording a reply does to the drawing (the backend's rule; only for its latest submission). */
export const REPLY_NEXT_STATUS = {
  APPROVED: 'COMPLETED',
  APPROVED_WITH_COMMENTS: 'COMPLETED_WITH_REMARKS',
  REVISE_AND_RESUBMIT: 'IN_PROGRESS',
  REJECTED: 'IN_PROGRESS',
};

/** 'A · Approved', 'Awaiting reply', 'Not recorded'. */
export function replyLabel(row) {
  if (row.replyState === 'awaiting') return 'Awaiting reply';
  if (row.replyState === 'not_recorded') return 'Not recorded';
  const code = REPLY_CODE_OF[row.reply];
  return code ? `${code} · ${labelize(row.reply, REPLY_LABELS)}` : labelize(row.reply, REPLY_LABELS);
}

/** Waiting-time buckets for the Submission Log's aging chips. */
export const AGE_BUCKETS = [
  { value: '90+', label: '90+ days', tone: 'red' },
  { value: '61-90', label: '61–90 days', tone: 'amber' },
  { value: '31-60', label: '31–60 days', tone: 'amber' },
  { value: '15-30', label: '15–30 days', tone: 'gray' },
  { value: '0-14', label: 'Last 14 days', tone: 'gray' },
  { value: 'check', label: 'Date needs check', tone: 'warn' },
];

export function ageBucket(sent) {
  if (sent.needsCheck || sent.age === null) return 'check';
  if (sent.age > 90) return '90+';
  if (sent.age > 60) return '61-90';
  if (sent.age > 30) return '31-60';
  if (sent.age >= 15) return '15-30';
  return '0-14';
}

/** Header facts of one transmittal (the submissions of one group). */
export function transmittalInfo(rows = []) {
  const first = rows[0] || {};
  const drawings = rows.map((row) => row.drawing);
  const awaiting = rows.filter((row) => row.replyState === 'awaiting');
  const ages = awaiting.map((row) => row.sent.age).filter((age) => age !== null && age !== undefined && age >= 0);
  return {
    project: mostCommon(drawings, (drawing) => drawing.project) || 'No project',
    projectNo: mostCommon(drawings, projectNumber),
    discs: disciplinesOf(drawings),
    count: rows.length,
    sent: first.sent,
    by: first.by,
    raws: [...new Set(rows.map((row) => row.sent.raw).filter(Boolean))],
    flagged: rows.some((row) => row.sent?.needsCheck),
    awaiting: awaiting.length,
    waitingDays: ages.length ? Math.max(...ages) : null,
    notRecorded: rows.filter((row) => row.replyState === 'not_recorded').length,
  };
}

/** Noun phrase for a transmittal: '3 LCS drawings', '1 HA drawing', '4 drawings'. */
export function transmittalNoun(info) {
  const disc = info.discs.length === 1 ? `${info.discs[0]} ` : '';
  return `${info.count} ${disc}${info.count === 1 ? 'drawing' : 'drawings'}`;
}

/* ------------------------------------------------------------------ wave 3: 13 #9 tidy the register */

/** Spellings of a placeholder Drawing ID: [{ value: '-', count: 117 }, …], most used first. */
export function placeholderIdSpellings(drawings = []) {
  const counts = new Map();
  drawings.forEach((drawing) => {
    if (!isPlaceholderDrawingId(drawing.drawingId)) return;
    const value = clean(drawing.drawingId) || '(empty)';
    counts.set(value, (counts.get(value) || 0) + 1);
  });
  return [...counts.entries()].map(([value, count]) => ({ value, count })).sort((a, b) => b.count - a.count || a.value.localeCompare(b.value));
}

/** The one way the register writes "no Drawing ID yet" (the backend's default). */
export const NO_ID_VALUE = 'NO ID';

const isAllCaps = (value) => /[A-Z]/.test(value) && value === value.toUpperCase();
const titleCase = (value) => value.toLowerCase().replace(/(^|[\s\-/(])([a-z])/g, (match, lead, char) => `${lead}${char.toUpperCase()}`);

/**
 * Drawing Type spellings to merge (13 #9): groups of spellings that are one type ('FLOOR PLAN' → 'Floor Plan',
 * 'SCHIMATIC' → 'Schematic'), merged into the most used spelling that is not all capitals. `caseOnly` groups are
 * single ALL-CAPS spellings that could be written in the usual case ('ROOM PLAN' → 'Room Plan'), offered unticked.
 * → [{ key, to, spellings: [{ value, count }], ids, caseOnly }], biggest change first.
 */
export function drawingTypeMerges(drawings = []) {
  const groups = new Map();
  drawings.forEach((drawing) => {
    const raw = clean(drawing.dtype);
    if (!raw) return;
    const key = drawingTypeKey(raw);
    const group = groups.get(key) || { key, spellings: new Map(), drawings: [] };
    group.spellings.set(raw, (group.spellings.get(raw) || 0) + 1);
    group.drawings.push(drawing);
    groups.set(key, group);
  });
  const result = [];
  groups.forEach((group) => {
    const spellings = [...group.spellings.entries()].map(([value, count]) => ({ value, count })).sort((a, b) => b.count - a.count);
    const mixed = spellings.find((item) => !isAllCaps(item.value));
    const to = mixed ? mixed.value : titleCase(spellings[0].value);
    const ids = group.drawings.filter((drawing) => clean(drawing.dtype) !== to).map((drawing) => drawing.id);
    if (!ids.length) return;
    result.push({ key: group.key, to, spellings, ids, caseOnly: spellings.length === 1 });
  });
  return result.sort((a, b) => Number(a.caseOnly) - Number(b.caseOnly) || b.ids.length - a.ids.length);
}

/**
 * Everything the "Tidy the register" callout and wizard offer:
 *   idChanges: drawings whose ID is a placeholder written another way than 'NO ID' (→ 'NO ID'),
 *   types: Drawing Type merges, people: projects with drawings without a Responsible (suggested: the person most
 *   of that project's drawings have), duplicates: real Drawing IDs used by more than one drawing (review only).
 */
export function tidySuggestions(drawings = []) {
  const spellings = placeholderIdSpellings(drawings);
  const noId = drawings.filter((drawing) => isPlaceholderDrawingId(drawing.drawingId));
  const idChanges = noId.filter((drawing) => clean(drawing.drawingId) !== NO_ID_VALUE).map((drawing) => drawing.id);
  const typeSpellings = new Set(drawings.map((drawing) => clean(drawing.dtype)).filter(Boolean));

  const byProject = new Map();
  drawings.forEach((drawing) => {
    const key = valueKey(drawing.project) || '__none__';
    if (!byProject.has(key)) byProject.set(key, []);
    byProject.get(key).push(drawing);
  });
  const people = [];
  byProject.forEach((list, key) => {
    const missing = list.filter((drawing) => !clean(drawing.resp));
    if (!missing.length) return;
    people.push({
      key,
      project: mostCommon(list, (drawing) => drawing.project) || 'No project',
      projectNo: mostCommon(list, projectNumber),
      ids: missing.map((drawing) => drawing.id),
      suggested: mostCommon(list, (drawing) => drawing.resp),
    });
  });
  people.sort((a, b) => b.ids.length - a.ids.length || a.project.localeCompare(b.project));

  const byId = new Map();
  drawings.forEach((drawing) => {
    const id = realDrawingId(drawing.drawingId);
    if (!id) return;
    const key = id.toUpperCase();
    if (!byId.has(key)) byId.set(key, { value: id, drawings: [] });
    byId.get(key).drawings.push(drawing);
  });
  const duplicates = [...byId.values()]
    .filter((entry) => entry.drawings.length > 1)
    .map((entry) => ({ ...entry, crossProject: new Set(entry.drawings.map((drawing) => valueKey(drawing.project))).size > 1 }))
    .sort((a, b) => Number(b.crossProject) - Number(a.crossProject) || b.drawings.length - a.drawings.length || a.value.localeCompare(b.value));

  return {
    noIdCount: noId.length,
    idSpellings: spellings,
    idChanges,
    typeCount: typeSpellings.size,
    types: drawingTypeMerges(drawings),
    missingRespCount: drawings.filter((drawing) => !clean(drawing.resp)).length,
    people,
    duplicates,
  };
}

/**
 * /bulk-edit payloads for the fixes a person accepted: every drawing appears once with all its changes (the
 * endpoint allows each drawing once per request), in chunks of `chunk` (the endpoint takes 500).
 * `fixes`: [{ ids, field, value, note }] → [[{ id, drawingId?, dtype?, resp?, change_note }]]
 */
export function buildTidyChanges(drawings = [], fixes = [], { chunk = 500 } = {}) {
  const byId = new Map(drawings.map((drawing) => [drawing.id, drawing]));
  const changes = new Map();
  fixes.forEach((fix) => {
    fix.ids.forEach((id) => {
      const drawing = byId.get(id);
      if (!drawing || clean(drawing[fix.field]) === clean(fix.value)) return;
      const change = changes.get(id) || { id, notes: [] };
      change[fix.field] = fix.value;
      change.notes.push(fix.note);
      changes.set(id, change);
    });
  });
  const list = [...changes.values()].map(({ notes, ...change }) => ({ ...change, change_note: `Tidy the register: ${[...new Set(notes)].join('; ')}` }));
  const chunks = [];
  for (let index = 0; index < list.length; index += chunk) chunks.push(list.slice(index, index + chunk));
  return chunks;
}

/* ------------------------------------------------------------------ wave 3: 13 #10 matrix */

/**
 * Where a floor / area sits in the building, for ordering matrix rows bottom-up:
 * basements (B2 < B1) → ground → mezzanine → podiums → numbered floors / levels → unknown → roof → top roof.
 */
export function floorRank(label) {
  const text = clean(label).toLowerCase();
  if (!text) return 8000;
  if (/top\s*roof|upper\s*roof/.test(text)) return 10000;
  if (/\broof\b/.test(text)) return 9000;
  const basement = text.match(/basement\s*(?:floor\s*)?-?\s*(\d+)?|\bb\s*-?\s*(\d)\b|\blower\s*ground/);
  if (basement) return -10 * (Number(basement[1] || basement[2] || 1));
  if (/\bground\b|\bgf\b/.test(text)) return 0;
  if (/mezz/.test(text)) return 5;
  const podium = text.match(/(\d+)\s*(?:st|nd|rd|th)?\s*podium|podium\s*-?\s*(\d+)?/);
  if (podium) return 10 + Number(podium[1] || podium[2] || 1) / 10;
  const level = text.match(/level\s*-?\s*(\d+)|\bl\s*-?(\d+)\b|(\d+)\s*(?:st|nd|rd|th)?\s*(?:floor|flr|fl\b)|floor\s*-?\s*(\d+)|^(\d+)\s*(?:st|nd|rd|th)\b/);
  if (level) return 100 + Number(level[1] || level[2] || level[3] || level[4] || level[5]);
  return 8000;
}

/** Short status words for matrix cells. */
const MATRIX_LABELS = {
  COMPLETED: 'Completed',
  COMPLETED_WITH_REMARKS: 'Done w/ rem.',
  APPROVED: 'Approved',
  APPROVED_WITH_COMMENTS: 'Appr. w/ c.',
  SUBMITTED: 'Submitted',
  IN_PROGRESS: 'In progress',
  PENDING: 'Pending',
  NOT_STARTED: 'Not started',
  ON_HOLD: 'On hold',
  BLOCKED: 'Blocked',
  REJECTED: 'Rejected',
  CANCELLED: 'Cancelled',
};
export const matrixLabel = (status) => MATRIX_LABELS[clean(status).toUpperCase()] || labelize(status, STATUS_LABELS);

/**
 * One project as a matrix: rows = Block / Floor (bottom-up, see floorRank), columns = disciplines, cells = the
 * drawings at that floor for that discipline.
 * → { rows: [{ key, label, sublabel }], columns: [{ key, label }], cell(rowKey, disc) → drawings[] }
 */
export function buildMatrix(drawings = []) {
  const rows = new Map();
  const cells = new Map();
  const discCounts = new Map();
  drawings.forEach((drawing) => {
    const floor = isPlaceholderText(drawing.floor) ? '' : clean(drawing.floor);
    const block = isPlaceholderText(drawing.block) ? '' : clean(drawing.block);
    const label = floor || block || drawingTitle(drawing);
    const sublabel = floor && block && !valueKey(floor).includes(valueKey(block)) ? block : '';
    const key = `${valueKey(label)}|${valueKey(sublabel)}`;
    if (!rows.has(key)) {
      const rank = floorRank(label);
      rows.set(key, { key, label, sublabel, rank: rank === 8000 && block ? floorRank(block) : rank });
    }
    const disc = clean(drawing.disc).toUpperCase() || 'OTHER';
    discCounts.set(disc, (discCounts.get(disc) || 0) + 1);
    const cellKey = `${key}§${disc}`;
    if (!cells.has(cellKey)) cells.set(cellKey, []);
    cells.get(cellKey).push(drawing);
  });
  const orderedRows = [...rows.values()].sort((a, b) => (
    a.rank - b.rank
    || a.label.localeCompare(b.label, undefined, { numeric: true })
    || a.sublabel.localeCompare(b.sublabel)
  ));
  const columns = [...discCounts.entries()]
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .map(([key]) => ({ key, label: key.replace(/_/g, ' ') }));
  return {
    rows: orderedRows.map(({ rank, ...row }) => row),
    columns,
    cell: (rowKey, disc) => cells.get(`${rowKey}§${disc}`) || [],
  };
}

/** The least advanced drawing of a cell decides its colour (what still holds the floor back). */
export function bottleneckOf(drawings = []) {
  return [...drawings].sort((a, b) => statusRank(a.status) - statusRank(b.status))[0] || null;
}

/* ------------------------------------------------------------------ wave 3: 14 #7 action vs status */

const APPROVED_LIKE = new Set(['APPROVED', 'COMPLETED', 'APPROVED_WITH_COMMENTS', 'COMPLETED_WITH_REMARKS']);
const normalizeNote = (value) => clean(value).toLowerCase().replace(/[‐-―]/g, '-').replace(/\s+/g, ' ');

/*
 * The 14 #7 rules only suggest a status when the note plainly says it happened. The Review dialog ticks every
 * suggestion, so a loose rule would move drawings that really are blocked: "To be submitted", "Not yet submitted",
 * "will be submitted after layouts", "Approved subject to site check" must NOT match.
 */
// Negated, future or conditional wording: the thing has not happened (yet).
const NOT_YET = /\b(not|never|yet|to be|will|would|shall|should|must|need(s|ed)? to|before|once|until|unless|if|subject to|expected|planned|plan to|tbc|tbd)\b|\b(pending|awaiting) (re-?)?submission\b|\?/;
// "Approved pending the hard copy": a condition, not a closed drawing.
const CONDITIONAL = /\bpending\b/;
// The consultant sent it back: never "approved" or "submitted", whatever else the note says.
const SENT_BACK = /\b(code\s*[-:]?\s*[cd]|rejected|revise|resubmit|re-submit|returned)\b/;
// "Submitted via email – awaiting approval", "Submitted on 3 Sep", "was submitted to WSP", "already submitted".
const SAYS_SUBMITTED = /^(re-?)?submitted\b|\b(was|were|has been|have been|already|been) (re-?)?submitted\b/;
// "APPROVED", "Approved by consultant", "approved – code A". With comments / as noted / code B → its own status.
const SAYS_APPROVED = /^approved\b|^code\s*[-:]?\s*[ab]\b/;
const WITH_COMMENTS = /\b(with|w\/) (minor )?(comments?|remarks?)\b|\bas noted\b|\bcode\s*[-:]?\s*b\b/;
const SAYS_CODE_C = /\bcode\s*[-:]?\s*c\b/;

/**
 * When the Action note and the status disagree, what to do about it (14 #7):
 *   approved  — blocked, but the action says "APPROVED"            → close as Approved (and clear the blocker);
 *               "approved with comments / as noted / code B"       → close as Approved w/ comments
 *   submitted — blocked, but the action says it was submitted       → move to Submitted (and clear the blocker)
 *   code_c    — blocked without a reason, the action says "CODE C"  → reason "Consultant comments (code C)"
 *   stale     — approved / completed but still carries a blocker    → clear the blocker
 * Negated / future / conditional notes ("to be submitted", "not yet approved") and notes saying the consultant sent
 * it back never suggest a status. → { kind, label, short, tone, change: { status?, blocker? }, note } or null.
 */
export function mismatchOf(drawing = {}) {
  const status = clean(drawing.status).toUpperCase();
  const blocker = clean(drawing.blocker).toUpperCase() || 'NONE';
  const action = normalizeNote(drawing.action);
  if (status === 'BLOCKED' && action) {
    const settled = !NOT_YET.test(action) && !SENT_BACK.test(action);
    if (settled && SAYS_APPROVED.test(action) && !CONDITIONAL.test(action)) {
      if (WITH_COMMENTS.test(action)) {
        return { kind: 'approved', label: 'Says approved w/ comments: close as such?', short: 'Close as Approved w/ comments', tone: 'amber', change: { status: 'APPROVED_WITH_COMMENTS', blocker: 'NONE' }, note: 'Closed as Approved w/ comments: the action note said approved with comments' };
      }
      return { kind: 'approved', label: 'Says approved: close as Approved?', short: 'Close as Approved', tone: 'amber', change: { status: 'APPROVED', blocker: 'NONE' }, note: 'Closed as Approved: the action note said approved' };
    }
    if (settled && SAYS_SUBMITTED.test(action)) {
      return { kind: 'submitted', label: 'Looks submitted: move to Submitted?', short: 'Move to Submitted', tone: 'amber', change: { status: 'SUBMITTED', blocker: 'NONE' }, note: 'Moved to Submitted: the action note said it was submitted' };
    }
    if (SAYS_CODE_C.test(action) && blocker === 'NONE') {
      return { kind: 'code_c', label: 'Suggest reason: code C', short: 'Set reason: Consultant comments', tone: 'navy', change: { blocker: 'CONSULTANT_COMMENTS' }, note: 'Reason set from the action note (code C)' };
    }
  }
  if (APPROVED_LIKE.has(status) && blocker !== 'NONE') {
    const done = status.startsWith('COMPLETED') ? 'completed' : 'approved';
    return {
      kind: 'stale',
      label: `${labelize(status, STATUS_LABELS)}, but still blocked by ${labelize(blocker, BLOCKER_LABELS)}: clear the blocker?`,
      short: 'Clear the blocker',
      tone: 'amber',
      change: { blocker: 'NONE' },
      note: `Blocker cleared: the drawing is ${done}`,
    };
  }
  return null;
}

export const MISMATCH_KINDS = [
  { kind: 'approved', title: 'Blocked, but the action says “APPROVED”', action: 'Close as Approved (w/ comments when it says so)' },
  { kind: 'submitted', title: 'Blocked, but the action says it was submitted', action: 'Move to Submitted' },
  { kind: 'stale', title: 'Approved or completed, but a blocker is still recorded', action: 'Clear the blocker' },
  { kind: 'code_c', title: 'No reason, but the action says “CODE C”', action: 'Reason: Consultant comments (code C)' },
];

/** { approved: [drawings], submitted: […], stale: […], code_c: […] } */
export function mismatchGroups(drawings = []) {
  const groups = Object.fromEntries(MISMATCH_KINDS.map((item) => [item.kind, []]));
  drawings.forEach((drawing) => {
    const found = mismatchOf(drawing);
    if (found) groups[found.kind].push(drawing);
  });
  return groups;
}

/* ------------------------------------------------------------------ wave 3: 14 #9 + 15 #9 follow-up emails */

/** 'mailto:' link for the person's own mail app ('' when the text is too long for a mailto link). */
export function mailtoHref({ to = '', cc = '', subject = '', body = '' } = {}) {
  const params = [];
  if (cc) params.push(`cc=${encodeURIComponent(cc)}`);
  if (subject) params.push(`subject=${encodeURIComponent(subject)}`);
  if (body) params.push(`body=${encodeURIComponent(body)}`);
  const href = `mailto:${encodeURIComponent(to).replace(/%40/g, '@').replace(/%2C/gi, ',')}${params.length ? `?${params.join('&')}` : ''}`;
  return href.length > 1900 ? '' : href;
}

/** The ready email of "Request info" for blocked drawings (one project or several). */
export function requestInfoEmail(drawings = [], { sender = '', now } = {}) {
  const projects = [...new Set(drawings.map((drawing) => clean(drawing.project)).filter(Boolean))];
  const where = projects.length === 1 ? projects[0] : `${projects.length} projects`;
  const one = drawings.length === 1;
  const subject = one
    ? `Information needed: ${drawingTitle(drawings[0])}${projects[0] ? ` — ${projects[0]}` : ''}`
    : `Information needed for ${drawings.length} blocked shop drawings — ${where}`;
  const lines = drawings.slice(0, 40).map((drawing) => {
    const since = blockedSince(drawing, { now });
    const reason = blockerMeta(drawing);
    const title = drawingTitle(drawing);
    const place = [drawing.block, drawing.floor].map(clean).filter((value) => value && value !== '-' && value !== title).join(' · ');
    const id = realDrawingId(drawing.drawingId);
    return [
      `- ${title}${place ? ` (${place})` : ''}`,
      id ? ` · ${id}` : '',
      reason.label && !reason.missing ? ` · ${reason.label}` : '',
      since && since.days !== null && since.days !== undefined ? ` · blocked ${formatDays(since.days)}` : '',
    ].join('');
  });
  if (drawings.length > 40) lines.push(`- … and ${drawings.length - 40} more`);
  const body = [
    'Hello,',
    '',
    `We can't continue the following shop drawing${one ? '' : 's'}${projects.length === 1 ? ` for ${projects[0]}` : ''}:`,
    '',
    ...lines,
    '',
    'What we need: ',
    '',
    'Could you please send it, or tell us when we can expect it?',
    '',
    'Kind regards,',
    displayPerson(sender),
  ].join('\n').trim();
  return { subject, body };
}

/** Submissions waiting more than `days` (with a readable date): the ones to chase. */
export const isOverdue = (row, days = 90) => row.replyState === 'awaiting' && !row.sent.needsCheck && row.sent.age !== null && row.sent.age > days;

/**
 * Overdue submissions grouped by who they were sent to, per project (one email each):
 * → [{ key, project, projectNo, recipient, rows, oldestDays, discs }], longest waiting first.
 */
export function chaseGroups(rows = [], { days = 90 } = {}) {
  const groups = new Map();
  rows.filter((row) => isOverdue(row, days)).forEach((row) => {
    const recipient = clean(row.to) || 'Consultant';
    const key = `${valueKey(row.drawing.project) || '__none__'}|${valueKey(recipient)}`;
    if (!groups.has(key)) groups.set(key, { key, project: clean(row.drawing.project) || 'No project', recipient, rows: [] });
    groups.get(key).rows.push(row);
  });
  return [...groups.values()].map((group) => ({
    ...group,
    projectNo: mostCommon(group.rows.map((row) => row.drawing), projectNumber),
    oldestDays: Math.max(...group.rows.map((row) => row.sent.age)),
    discs: disciplinesOf(group.rows.map((row) => row.drawing)),
    rows: [...group.rows].sort((a, b) => b.sent.age - a.sent.age),
  })).sort((a, b) => b.oldestDays - a.oldestDays || b.rows.length - a.rows.length);
}

/** The ready follow-up email for one chase group (or one submission). */
export function chaseEmail(group, { sender = '' } = {}) {
  const count = group.rows.length;
  const subject = `Follow-up: ${count} shop drawing${count === 1 ? '' : 's'} awaiting your reply — ${group.project}`;
  const lines = group.rows.slice(0, 60).map((row, index) => {
    const id = realDrawingId(row.drawing.drawingId);
    const waiting = row.sent.age !== null && row.sent.age !== undefined ? ` · ${formatDays(row.sent.age)} waiting` : '';
    return `${index + 1}. ${id ? `${id} — ` : ''}${drawingTitle(row.drawing)} (${clean(row.drawing.disc).toUpperCase() || 'OTHER'}) · sent ${row.sent.label}${waiting}`;
  });
  if (count > 60) lines.push(`… and ${count - 60} more`);
  const recipient = clean(group.recipient);
  const body = [
    `Dear ${recipient && recipient.toLowerCase() !== 'consultant' ? recipient : 'Consultant'},`,
    '',
    `The following shop drawing${count === 1 ? '' : 's'} for ${group.project}${group.projectNo ? ` (#${group.projectNo})` : ''} ${count === 1 ? 'was' : 'were'} submitted to you and ${count === 1 ? 'is' : 'are'} still awaiting your reply:`,
    '',
    ...lines,
    '',
    'Could you please send your reply (code A / B / C / D with comments), or let us know when we can expect it?',
    '',
    'Kind regards,',
    displayPerson(sender),
  ].join('\n').trim();
  return { subject, body };
}

/**
 * Turnaround per project and recipient (15 #9): how long replies took (when the reply date is known) and how long
 * the open ones wait. → { rows: [{ key, project, projectNo, recipient, submissions, replied, avgReplyDays, awaiting,
 * overdue, oldestWaiting, medianWaiting }], slowest } — slowest first.
 */
export function turnaroundStats(rows = []) {
  const groups = new Map();
  rows.forEach((row) => {
    const recipient = clean(row.to) || 'Consultant';
    const key = `${valueKey(row.drawing.project) || '__none__'}|${valueKey(recipient)}`;
    if (!groups.has(key)) groups.set(key, { key, project: clean(row.drawing.project) || 'No project', recipient, rows: [] });
    groups.get(key).rows.push(row);
  });
  const median = (values) => {
    if (!values.length) return null;
    const sorted = [...values].sort((a, b) => a - b);
    const middle = Math.floor(sorted.length / 2);
    return sorted.length % 2 ? sorted[middle] : Math.round((sorted[middle - 1] + sorted[middle]) / 2);
  };
  const result = [...groups.values()].map((group) => {
    const replyDays = group.rows
      .filter((row) => row.replyState === 'replied' && row.replyDate?.iso && row.sent.iso && !row.sent.needsCheck)
      .map((row) => Math.round((Date.parse(row.replyDate.iso) - Date.parse(row.sent.iso)) / 86400000))
      .filter((days) => days >= 0);
    const waiting = group.rows.filter((row) => row.replyState === 'awaiting' && !row.sent.needsCheck && row.sent.age !== null).map((row) => row.sent.age);
    return {
      key: group.key,
      project: group.project,
      projectNo: mostCommon(group.rows.map((row) => row.drawing), projectNumber),
      recipient: group.recipient,
      submissions: group.rows.length,
      replied: replyDays.length,
      avgReplyDays: replyDays.length ? Math.round(replyDays.reduce((sum, days) => sum + days, 0) / replyDays.length) : null,
      awaiting: group.rows.filter((row) => row.replyState === 'awaiting').length,
      overdue: waiting.filter((days) => days > 90).length,
      oldestWaiting: waiting.length ? Math.max(...waiting) : null,
      medianWaiting: median(waiting),
    };
  });
  const score = (item) => Math.max(item.avgReplyDays ?? -1, item.medianWaiting ?? -1);
  result.sort((a, b) => score(b) - score(a) || b.awaiting - a.awaiting || a.project.localeCompare(b.project));
  return { rows: result, slowest: result.find((item) => score(item) >= 0) || null };
}
